import { Ticket } from "@signalist/shared-types";

export enum TicketActivityKind {
	FILED = "FILED",
	CUSTOMER_REPLIED = "CUSTOMER_REPLIED",
	SUPPORT_REPLIED = "SUPPORT_REPLIED",
	RESOLVED = "RESOLVED",
	CLOSED = "CLOSED",
}

export interface TicketActivity {
	at: string;
	kind: TicketActivityKind;
}

/**
 * What the "Last activity" cell says. Later timestamps win; on a tie the event listed later here
 * does, so a ticket resolved by a staff reply reads "Marked resolved" rather than "Signalist
 * replied". A fresh ticket carries `lastCustomerActivityAt === createdAt` (the API stamps it on
 * creation, `ticket.service.ts`), which must read "Filed", not "You replied".
 */
export const lastTicketActivity = (ticket: Ticket): TicketActivity => {
	const candidates: TicketActivity[] = [
		{ at: ticket.createdAt, kind: TicketActivityKind.FILED },
		...(ticket.lastCustomerActivityAt && ticket.lastCustomerActivityAt !== ticket.createdAt
			? [{ at: ticket.lastCustomerActivityAt, kind: TicketActivityKind.CUSTOMER_REPLIED }]
			: []),
		...(ticket.lastStaffActivityAt
			? [{ at: ticket.lastStaffActivityAt, kind: TicketActivityKind.SUPPORT_REPLIED }]
			: []),
		...(ticket.resolvedAt ? [{ at: ticket.resolvedAt, kind: TicketActivityKind.RESOLVED }] : []),
		...(ticket.closedAt ? [{ at: ticket.closedAt, kind: TicketActivityKind.CLOSED }] : []),
	];

	return candidates.reduce((latest, candidate) =>
		Date.parse(candidate.at) >= Date.parse(latest.at) ? candidate : latest
	);
};

/** The filer as the subject sub-line shows them. */
export const ticketFilerName = (ticket: Ticket): string =>
	[ticket.createdBy?.firstName, ticket.createdBy?.lastName].filter(Boolean).join(" ") ||
	ticket.createdBy?.email ||
	"";

/**
 * The linked record as the row shows it: a type key for the i18n string and the raw id
 * ("Campaign #412"). The DTO carries no entity name and campaign routes key off uuids, so nothing
 * here is a link (PREC-3937 decision 10).
 */
export const ticketLinkedEntityKey = (ticket: Ticket): { key: string; id: number } | null =>
	ticket.linkedEntityType && ticket.linkedEntityId
		? { key: ticket.linkedEntityType, id: ticket.linkedEntityId }
		: null;
